import { Box, Stack, TextField, Typography, alpha } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { PasswordField } from "src/shared/components";
import { LoadingButton } from "src/shared/components/buttons/LoadingButton";
import { buttons, inputs } from "src/shared/constants";
import { setUserAuthentication } from "src/shared/lib/store/slices/auth";
import { useTypedDispatch } from "src/shared/lib/store/store";
import { theme } from "src/shared/theme/theme";
import { messages } from "src/shared/utils/hookFormRules";
import { getRuleSizeAndLength } from "src/shared/utils/hookFormRules/string";
import { useLazyAuthSignInQuery } from "../model/api/signIn";
import {
  IBlurMethod,
  TLoginData,
  TAuthenticationSteps,
} from "../model/types/types";

export interface ISignInFormProps extends IBlurMethod {
  handleNextStep: (values: TAuthenticationSteps) => void;
}

export const SignInForm: FC<ISignInFormProps> = ({
  handleNextStep,
  handleFocus,
}) => {
  const [errorMessage, setErrorMessage] = useState("");
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();

  const [sendAuthSign, { isFetching, isError, error, isSuccess, data }] =
    useLazyAuthSignInQuery();

  useEffect(() => {
    if (isSuccess && data) {
      const { with_2fa } = data.data;

      if (with_2fa) {
        handleNextStep(2);
      } else {
        dispatch(setUserAuthentication(true));
        navigate("/");
      }
    }
  }, [isSuccess, data, dispatch, navigate, handleNextStep]);

  useEffect(() => {
    if (isError && error && "status" in error) {
      const { status } = error;

      setErrorMessage(
        status !== 429
          ? "Логин и/или пароль указаны не верно"
          : "Слишком много запросов",
      );
    }
  }, [isError, error]);

  const clearMessageError = () => setErrorMessage("");

  const methods = useForm<TLoginData>({
    defaultValues: { password: "", login: "" },
    mode: "all",
  });

  const {
    handleSubmit,
    control,
    formState: { isDirty, isValid, errors },
  } = methods;

  const isSubmitBtnDisabled = !isDirty || !isValid || isFetching;

  const handleSave = (value: TLoginData) => {
    sendAuthSign(value);
  };

  return (
    <Box
      flex={1}
      minHeight={0}
      height="100%"
      component="form"
      px={7}
      display="flex"
      alignItems="center"
      onSubmit={handleSubmit(handleSave)}
    >
      <Box
        position="relative"
        zIndex={2}
        width={{ xs: "auto", sm: 420 }}
        px={6}
        py={8}
        borderRadius={1}
        bgcolor={alpha("#000000", 0.68)}
      >
        <Stack spacing={6} alignItems="center">
          <Typography
            component="h5"
            variant="h5"
            textAlign="center"
            color="textPrimary"
          >
            Вход в кабинет
          </Typography>
          <Stack spacing={4} alignItems="center">
            <Controller
              control={control}
              name="login"
              render={({ field: { ref, ...field }, fieldState }) => (
                <TextField
                  inputRef={ref}
                  fullWidth
                  label={inputs.login}
                  {...field}
                  helperText={fieldState.error?.message}
                  error={!!errors.login}
                  size="small"
                  onFocus={() => {
                    clearMessageError();
                    handleFocus(true);
                  }}
                  onBlur={() => handleFocus(false)}
                />
              )}
              rules={{
                required: messages.required,
                maxLength: getRuleSizeAndLength(256, messages.maxLength),
              }}
            />
            <Controller
              control={control}
              name="password"
              render={({ field: { ref, ...field }, fieldState }) => (
                <PasswordField
                  inputRef={ref}
                  label={inputs.password}
                  {...field}
                  helperText={fieldState.error?.message}
                  onFocus={() => {
                    clearMessageError();
                    handleFocus(true);
                  }}
                  onBlur={() => handleFocus(false)}
                  error={!!errors.password}
                  size="small"
                />
              )}
              rules={{
                required: messages.required,
                maxLength: getRuleSizeAndLength(256, messages.maxLength),
              }}
            />
          </Stack>

          {errorMessage && (
            <Typography textAlign="center" color={theme.palette.error.light}>
              {errorMessage}
            </Typography>
          )}
          <LoadingButton
            color={"warning"}
            variant={"contained"}
            isLoading={isFetching}
            disabled={isSubmitBtnDisabled}
          >
            {buttons.continue}
          </LoadingButton>
        </Stack>
      </Box>
    </Box>
  );
};
